import { useEffect, useState } from 'react';

import { useStoreContext } from 'features/store';
import { isEmpty } from 'lodash-es';

import { ItemAvailabilityStatus } from 'enums/menu';
import usePosVendor from 'hooks/menu/use-pos-vendor';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useLoyaltyIncentivesAvailabilityUtils } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isEntryAvailable } from 'state/unified-menu/utils';
import { IMenuObjectWithDaypart, getAvailabilityStatus, itemIsAvailable } from 'utils/availability';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';
import { IWithVendorConfig } from 'utils/vendor-config';

import { useLoyaltyContext } from '../loyalty-context';

import { IncentiveEvaluationMap } from './types';
import {
  buildInCooldownEvaluationResult,
  buildOutOfDayPartEvaluationResult,
  buildVendorEvaluationResult,
} from './utils/incentives-availability';

export const useRewardsEvaluation = () => {
  const { engineRewardsMap, sanityRewardsMap } = useLoyaltyRewardsList();
  const { prices, store } = useStoreContext();
  const { vendor } = usePosVendor();
  const { activeDayParts, dayParts } = useMainMenuContext();
  const [rewardsEvaluationMap, setRewardsEvaluationMap] = useState<IncentiveEvaluationMap>({});
  const { rewardsCooldown } = useLoyaltyContext();
  const { checkIncentiveAvailability } = useLoyaltyIncentivesAvailabilityUtils();

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();
  const { indexedMenu, indexedRewards, isLoading: isUnifiedMenuLoading } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();

  useEffect(() => {
    if (
      isEmpty(sanityRewardsMap) ||
      isEmpty(engineRewardsMap) ||
      (!isUnifiedMenuEnabled && !prices) ||
      (isUnifiedMenuEnabled && (!indexedMenu || !posServiceMode)) ||
      (isUnifiedMenuEnabled && isUnifiedMenuLoading)
    ) {
      return;
    }

    const buildEvaluationResultsMap = () => {
      const evaluationsMap = Object.values(sanityRewardsMap ?? {}).reduce(
        (evaluationMap, sanityReward) => {
          const loyaltyEngineId = sanityReward?.loyaltyEngineId ?? '';
          const engineReward = engineRewardsMap[loyaltyEngineId];

          if (!engineReward || engineReward?.locked) {
            evaluationMap[loyaltyEngineId] = [];
            return evaluationMap;
          }

          let isRewardAvailable;
          if (isUnifiedMenuEnabled) {
            // naming convention is wrong this is jus checking the reward parent at this step
            isRewardAvailable = checkIncentiveAvailability(sanityReward._id);
          } else {
            isRewardAvailable = itemIsAvailable(sanityReward as IWithVendorConfig, vendor, prices);
          }
          if (!isRewardAvailable) {
            evaluationMap[loyaltyEngineId] = [buildVendorEvaluationResult()];
            return evaluationMap;
          }

          const incentive = sanityReward?.incentives?.[0] ?? undefined;
          let status: ItemAvailabilityStatus | null = null;
          if (isUnifiedMenuEnabled) {
            const rewardEntry = indexedRewards.find(sanityReward._id);
            const entryId = rewardEntry?.options[0].entryId;
            if (rewardEntry) {
              const menuEntry = indexedMenu.find(entryId);
              status = getOneMenuAvailability(
                menuEntry,
                activeDayParts,
                posServiceMode,
                Boolean(store.hasBurgersForBreakfast),
                isEntryAvailable
              );
            }
          } else {
            status = getAvailabilityStatus({
              isReward: true,
              data: incentive as IMenuObjectWithDaypart,
              activeDayParts,
              prices,
              vendor,
              isExtra: false,
              storeHasBurgersForBreakfast: Boolean(store.hasBurgersForBreakfast),
            });
          }

          let evaluationsResult = null;
          switch (status) {
            case ItemAvailabilityStatus.AVAILABLE:
            case ItemAvailabilityStatus.STORE_NOT_SELECTED:
              break;
            case ItemAvailabilityStatus.OUT_OF_DAYPART:
              evaluationsResult = buildOutOfDayPartEvaluationResult(sanityReward, dayParts);
              break;
            default:
              evaluationsResult = buildVendorEvaluationResult();
          }

          if (evaluationsResult) {
            evaluationMap[loyaltyEngineId] = [evaluationsResult];
          } else if (rewardsCooldown.isActive) {
            evaluationMap[loyaltyEngineId] = [buildInCooldownEvaluationResult()];
          }

          return evaluationMap;
        },
        {} as IncentiveEvaluationMap
      );

      return evaluationsMap;
    };

    setRewardsEvaluationMap(buildEvaluationResultsMap());
  }, [
    activeDayParts,
    dayParts,
    engineRewardsMap,
    indexedMenu,
    isUnifiedMenuEnabled,
    isUnifiedMenuLoading,
    posServiceMode,
    prices,
    sanityRewardsMap,
    store.hasBurgersForBreakfast,
    vendor,
    rewardsCooldown.isActive,
    checkIncentiveAvailability,
    indexedRewards,
  ]);

  return {
    rewardsEvaluationMap,
    engineRewardsMap,
    sanityRewardsMap,
  };
};
